import { type JSX, useEffect, useState } from "react";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GenericError } from "@via/components/error.tsx";
import TableContextMenu from "@via/components/table/table-context-menu.tsx";
import Table, { DrawerSize } from "@via/components/table/table.tsx";
import Drawer from "@via/components/drawer.tsx";
import useUrlParams from "@via/hooks/useURLParams.tsx";
import { useAuth } from "@via/hooks/useAuth.tsx";
import {
  useDeleteKnowledgeAssistant,
  useKnowledgeAssistantsList,
} from "@via/pages/cortex/knowledge-builder/knowledge-builder.api.tsx";
import {
  EMPTY_TABLE_MESSAGE,
  ERROR,
  USER_ROLES,
} from "@via/utils/constants.tsx";
import { ActionButton } from "@via/components/action-button/action-button.tsx";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import AddAssistantDrawer from "@via/pages/cortex/knowledge-builder/add-assistant-drawer";
import { TableContextMenuInterface } from "@via/utils/interfaces.tsx";
import { TruncatedText } from "@via/components/truncated-text.tsx";
import { Tooltip } from "react-tooltip";
import { classNames } from "@via/utils/helpers";
import type {
  KnowledgeBuilderAssistantListItem,
  KnowledgeBuilderIngestStatus,
} from "common";
import {
  getAssistantSourceFileNames,
  getWriteGroupIds,
} from "@via/pages/cortex/knowledge-builder/utils/helper";

const defaultParams = {
  sort: [{ id: "createdAt", desc: true }],
  filters: [],
  page: 0,
  pageSize: 10,
};

const KNOWLEDGE_BUILDER_TABLE_OPTIONS = [{ name: "Delete", href: "#" }];

const FoundationModelBadge = ({ model }: { model: string }) => {
  return (
    <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
      {model}
    </span>
  );
};

const StatusBadge = ({ status }: { status: KnowledgeBuilderIngestStatus }) => {
  const config = {
    processing: {
      label: "Processing",
      className: "bg-yellow-100 text-yellow-800",
    },
    ready: { label: "Ready", className: "bg-green-100 text-green-800" },
    failed: { label: "Failed", className: "bg-red-100 text-red-800" },
  }[status];

  return (
    <span
      className={classNames(
        config.className,
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
      )}>
      {config.label}
    </span>
  );
};

const KnowledgeBuilder = (): JSX.Element => {
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();
  const { user } = useAuth();
  const isAdmin = user?.userRole === USER_ROLES.ADMIN;

  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [columnFilters] = useState<Array<{ id: string; value: unknown }>>(
    initialParams.filters
  );
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [assistantToBeDeleted, setAssistantToBeDeleted] =
    useState<KnowledgeBuilderAssistantListItem | null>(null);

  const {
    data: knowledgeAssistantsList,
    isError: isKnowledgeAssistantsListError,
    isLoading: isKnowledgeAssistantsListLoading,
  } = useKnowledgeAssistantsList(page, pageSize, sort, columnFilters);

  const { mutateAsync: deleteAssistant, isPending: isDeleteAssistantLoading } =
    useDeleteKnowledgeAssistant();

  useEffect(() => {
    setUrlParams({ sort, filters: columnFilters, page, pageSize });
  }, [sort, columnFilters, page, pageSize]);

  const handleContextMenuAction = (
    name: string,
    assistant: KnowledgeBuilderAssistantListItem
  ): void => {
    if (name === "Delete") {
      setAssistantToBeDeleted(assistant);
    }
  };

  // Returning false keeps the dialog open. Resolving closes it through DeleteModal's
  // setItem, which is what clears the assistant, so there is one close path rather
  // than a success effect racing the dialog.
  const handleDeleteAssistant = async (): Promise<void | boolean> => {
    if (!assistantToBeDeleted) return false;
    await deleteAssistant(assistantToBeDeleted.id);
  };

  const columnHelper = createColumnHelper<KnowledgeBuilderAssistantListItem>();
  const columns = [
    columnHelper.accessor("name", {
      cell: ({ row }) => {
        return (
          <div className="flex flex-col">
            <span className="font-medium">{row.original.name}</span>
            <span className="text-sm text-gray-500">
              {row.original.prompt ? (
                <TruncatedText
                  id={`kb-prompt-${row.original.id}`}
                  text={row.original.prompt}
                  maxLength={80}
                  externalClassNames="cursor-default"
                />
              ) : (
                "-"
              )}
            </span>
          </div>
        );
      },
      header: () => <span>Assistant Name</span>,
      size: 250,
    }),
    columnHelper.accessor("model", {
      cell: info => <FoundationModelBadge model={info.getValue() ?? "-"} />,
      header: () => <span>Foundation Model</span>,
      size: 150,
      enableSorting: false,
    }),
    columnHelper.accessor("sourceCount", {
      cell: info => {
        const count = info.getValue() ?? 0;
        const fileNames = getAssistantSourceFileNames(info.row.original);
        const tooltipId = `kb-sources-${info.row.original.id}`;
        const content =
          fileNames.length > 0 ? fileNames.join("\n") : "No files uploaded";

        return (
          <span
            data-tooltip-id={tooltipId}
            data-tooltip-content={content}
            className="cursor-default">
            {count}
            <Tooltip
              id={tooltipId}
              className="z-50 max-w-xs whitespace-pre-line break-words"
            />
          </span>
        );
      },
      header: () => <span>Knowledge Sources</span>,
      size: 150,
      enableSorting: false,
    }),
    columnHelper.accessor("ingestStatus", {
      cell: info => <StatusBadge status={info.getValue() ?? "ready"} />,
      header: () => <span>Status</span>,
      size: 120,
      enableSorting: false,
    }),
    columnHelper.accessor("ingestErrorMessage", {
      cell: info => {
        const value = info.getValue() || "-";
        return (
          <span className="text-sm" title={value}>
            {value}
          </span>
        );
      },
      header: () => <span>Error</span>,
      size: 260,
      enableSorting: false,
    }),
    columnHelper.accessor("id", {
      cell: info => {
        const assistant = info.row.original;
        const userId = Number(user?.id);
        const userGroupIds: number[] = Array.isArray(user?.userGroupIds)
          ? user.userGroupIds
              .map((v: unknown) => Number(v))
              .filter((v: number) => Number.isFinite(v))
          : [];
        const writeGroupIds = getWriteGroupIds(assistant.writeGroupId);

        const hasWritePermission =
          isAdmin ||
          (Number.isFinite(userId) && assistant.ownerId === userId) ||
          writeGroupIds.some(id => userGroupIds.includes(id));

        if (!hasWritePermission) return "-";

        const options: TableContextMenuInterface[] =
          (assistant.ingestStatus ?? "ready") === "processing"
            ? [{ name: "Delete", href: "#", disabled: true }]
            : KNOWLEDGE_BUILDER_TABLE_OPTIONS;

        return (
          <TableContextMenu
            options={options}
            onSelect={option => {
              handleContextMenuAction(option.name, assistant);
            }}
          />
        );
      },
      header: () => <span>Actions</span>,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<KnowledgeBuilderAssistantListItem>>;

  return (
    <main className="py-4">
      <div className="via-page-heading-wrapper">
        <h2 className="via-h2">Knowledge Builder (RAG)</h2>
        <ActionButton
          button="Add Assistant"
          primary={true}
          onClick={() => {
            setIsAddDrawerOpen(true);
          }}
        />
      </div>

      <Drawer
        id="cortex-add-knowledge-assistant"
        open={isAddDrawerOpen}
        setOpen={setIsAddDrawerOpen}
        size={DrawerSize.Small}
        resizable={true}>
        <AddAssistantDrawer setOpen={setIsAddDrawerOpen} />
      </Drawer>

      <DeleteModal
        item={assistantToBeDeleted}
        setItem={setAssistantToBeDeleted}
        deleteFunction={handleDeleteAssistant}
        isLoading={isDeleteAssistantLoading}
        title="Delete Assistant"
      />

      {isKnowledgeAssistantsListError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <Table
          isLoading={isKnowledgeAssistantsListLoading}
          data={knowledgeAssistantsList?.data ?? []}
          columns={columns}
          sort={sort}
          setSort={setSort}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={knowledgeAssistantsList?.total}
          emptyMessage={EMPTY_TABLE_MESSAGE.DEFAULT}
          applyColumnSize={true}
        />
      )}
    </main>
  );
};

export default KnowledgeBuilder;
